// Pure Heist City game module — no framework dependencies.
// All functions are pure (no mutation); state is plain data.
// Imports types from the component directory since they're already well-defined there.

import type { MapState, GridType, Position, CharacterToken, PlayerSelection } from '../../components/games/heist-city/types';
import { loadMap } from '../../components/games/heist-city/data/mapLoader';
import {
  type HeistDeckId, type HeistDecksState, type HandDecksState, type PlayerHand,
  createHeistDecks, drawFromDeck, tallyStates, majorityDecks, getHeistCard,
  createHandDecks, dealFromHandDeck, returnToHandDeck, HEIST_CARD_BY_ID, HEIST_DECK_IDS,
} from './heistDecks';

export type { HeistDeckId, HeistDecksState, HeistCard, HeistDeckState, StateTally, HandDecksState, PlayerHand } from './heistDecks';
export {
  HEIST_DECK_IDS, HEIST_DECK_NAMES, DECK_STATE, HEIST_CARDS, HEIST_CARD_BY_ID,
  getHeistCard, tallyStates, majorityDecks,
} from './heistDecks';

// --- Config ---

export interface HeistCityConfig {
  mapId: string;
}

export const DEFAULT_CONFIG: HeistCityConfig = {
  mapId: 'bank-job-hex',
};

// --- State ---

export type Phase = 'playing' | 'complete';

export interface PlayerInit {
  id: string;
  name: string;
  isBot?: boolean;
}

export interface HeistCityPlayer {
  id: string;
  name: string;
  playerNumber: 1 | 2;
  isBot: boolean;
}

// Compatible with LogEntry from components/GameLog.tsx (structural match)
export interface HeistLogEntry {
  id: string;
  timestamp: number;
  type: 'dice-roll' | 'ai-action' | 'ai-plan' | 'npc-action';
  playerName: string;
  dice1?: number;
  dice2?: number;
  total?: number;
  characterName?: string;
  actionName?: string;
  targetName?: string;
  result?: string;
  reasoning?: string;
  npcType?: string;
  npcActionDesc?: string;
}

export interface HeistCityState {
  config: HeistCityConfig;
  phase: Phase;
  turnNumber: number;
  players: HeistCityPlayer[];
  mapState: MapState;
  gridType: GridType;
  alertModifier: number;
  lastDiceRoll: { dice1: number; dice2: number; total: number; roller: string } | null;
  playerSelections: PlayerSelection[];
  rulerState: { start: Position; end: Position; playerId: string } | null;
  logEntries: HeistLogEntry[];
  heistDecks: HeistDecksState;
  handDecks: HandDecksState;
  playerHands: Record<string, PlayerHand>;
  winnerId: string | null;
  rng: number;
}

// --- Actions ---

export type HeistCityAction =
  | { type: 'updateMapState'; playerId: string; mapState: MapState }
  | { type: 'updateCharacter'; playerId: string; characterId: string; updates: Partial<CharacterToken> }
  | { type: 'loadMap'; playerId: string; mapId: string; mapState: MapState; gridType: GridType }
  | { type: 'loadGame'; playerId: string; mapState: MapState; gridType: GridType; turnNumber: number; alertModifier: number }
  | { type: 'endTurn'; playerId: string }
  | { type: 'rollDice'; playerId: string; dice1: number; dice2: number; total: number }
  | { type: 'updateSelection'; playerId: string; characterId: string | null }
  | { type: 'updateRuler'; playerId: string; start: Position | null; end: Position | null }
  | { type: 'updateAlert'; playerId: string; alertModifier: number }
  | { type: 'drawHeistCard'; playerId: string; deck: HeistDeckId }
  | { type: 'drawRoundCards'; playerId: string }
  | { type: 'resetHeistDecks'; playerId: string }
  | { type: 'dealHand'; playerId: string }
  | { type: 'playCard'; playerId: string; cardId: string }
  | { type: 'reorderHand'; playerId: string; cards: string[] };

// --- View (no hidden info — both players see the full board) ---

export interface HeistCityView extends HeistCityState {
  viewPlayerId: string | null;
}

// --- Core API ---

export function createGame(
  config: HeistCityConfig,
  players: PlayerInit[],
  seed = Date.now()
): HeistCityState {
  const p1 = players[0];
  const p2 = players[1];
  const loadedMap = loadMap(config.mapId, p1?.id ?? 'player-1', p2?.id ?? 'player-2');

  const heistPlayers: HeistCityPlayer[] = players.slice(0, 2).map((p, i) => ({
    id: p.id,
    name: p.name,
    playerNumber: (i + 1) as 1 | 2,
    isBot: p.isBot ?? false,
  }));

  const [heistDecks, rng0] = createHeistDecks(seed | 0);
  const [handDecks, rng] = createHandDecks(rng0);

  const playerHands: Record<string, PlayerHand> = {};
  for (const p of heistPlayers) playerHands[p.id] = { cards: [], activePlayedCard: null };

  return {
    config,
    phase: 'playing',
    turnNumber: 1,
    players: heistPlayers,
    mapState: loadedMap,
    gridType: loadedMap.gridType,
    alertModifier: 0,
    lastDiceRoll: null,
    playerSelections: [],
    rulerState: null,
    logEntries: [],
    heistDecks,
    handDecks,
    playerHands,
    winnerId: null,
    rng,
  };
}

export function applyAction(state: HeistCityState, action: HeistCityAction): HeistCityState {
  switch (action.type) {
    case 'updateMapState':
      return { ...state, mapState: action.mapState };

    case 'updateCharacter':
      return {
        ...state,
        mapState: {
          ...state.mapState,
          characters: state.mapState.characters.map(c =>
            c.id === action.characterId ? { ...c, ...action.updates } : c
          ),
        },
      };

    case 'loadMap': {
      // Preserve equipment/actions from current characters; reset VP
      const updatedCharacters = action.mapState.characters.map(newChar => {
        const oldChar = state.mapState.characters.find(c =>
          c.playerNumber === newChar.playerNumber && c.role === newChar.role
        );
        return {
          ...newChar,
          equipment: oldChar?.equipment ?? [],
          actions: oldChar?.actions ?? [],
          victoryPoints: 0,
        };
      });
      return {
        ...state,
        config: { ...state.config, mapId: action.mapId },
        mapState: { ...action.mapState, characters: updatedCharacters },
        gridType: action.gridType,
        turnNumber: 1,
        playerSelections: [],
      };
    }

    case 'loadGame':
      return {
        ...state,
        mapState: action.mapState,
        gridType: action.gridType,
        turnNumber: action.turnNumber,
        alertModifier: action.alertModifier,
        playerSelections: [],
      };

    case 'endTurn': {
      const newTurn = state.turnNumber + 1;
      const updatedCharacters = state.mapState.characters.map(char => ({
        ...char,
        exhausted: char.state === 'Unconscious' ? true : false,
        actions: [],
      }));
      return {
        ...state,
        turnNumber: newTurn,
        mapState: { ...state.mapState, characters: updatedCharacters },
        playerSelections: [],
      };
    }

    case 'rollDice': {
      const roller = state.players.find(p => p.id === action.playerId);
      const rollerName = roller?.name ?? action.playerId;
      const entry: HeistLogEntry = {
        id: `roll-${Date.now()}-${action.dice1}${action.dice2}`,
        timestamp: Date.now(),
        type: 'dice-roll',
        playerName: rollerName,
        dice1: action.dice1,
        dice2: action.dice2,
        total: action.total,
      };
      return {
        ...state,
        lastDiceRoll: { dice1: action.dice1, dice2: action.dice2, total: action.total, roller: action.playerId },
        logEntries: [...state.logEntries, entry],
      };
    }

    case 'updateSelection': {
      const player = state.players.find(p => p.id === action.playerId);
      const playerNumber: 1 | 2 | 'observer' = player?.playerNumber ?? 'observer';
      const updatedSelections: PlayerSelection[] = action.characterId
        ? [
            ...state.playerSelections.filter(s => s.playerId !== action.playerId),
            { playerId: action.playerId, characterId: action.characterId, playerNumber },
          ]
        : state.playerSelections.filter(s => s.playerId !== action.playerId);
      return { ...state, playerSelections: updatedSelections };
    }

    case 'updateRuler':
      return {
        ...state,
        rulerState: action.start && action.end
          ? { start: action.start, end: action.end, playerId: action.playerId }
          : null,
      };

    case 'updateAlert':
      return { ...state, alertModifier: action.alertModifier };

    case 'drawHeistCard': {
      const [deckState, rng] = drawFromDeck(state.heistDecks[action.deck], state.rng);
      return {
        ...state,
        heistDecks: { ...state.heistDecks, [action.deck]: deckState },
        rng,
        logEntries: [...state.logEntries, heistDrawLog(state, action.playerId, action.deck, deckState.activeCardId)],
      };
    }

    case 'drawRoundCards': {
      const tally = tallyStates(state.mapState.characters.map(c => c.state));
      const decksToDraw = majorityDecks(tally);
      if (decksToDraw.length === 0) return state;

      let heistDecks = state.heistDecks;
      let rng = state.rng;
      const newLogs: HeistLogEntry[] = [];
      for (const deck of decksToDraw) {
        const [deckState, nextRng] = drawFromDeck(heistDecks[deck], rng);
        rng = nextRng;
        heistDecks = { ...heistDecks, [deck]: deckState };
        newLogs.push(heistDrawLog(state, action.playerId, deck, deckState.activeCardId));
      }
      return { ...state, heistDecks, rng, logEntries: [...state.logEntries, ...newLogs] };
    }

    case 'resetHeistDecks': {
      const [heistDecks, rng] = createHeistDecks(state.rng);
      return { ...state, heistDecks, rng };
    }

    case 'dealHand': {
      const player = state.players.find(p => p.id === action.playerId);
      if (!player) return state;
      const myChars = state.mapState.characters.filter(c => c.playerNumber === player.playerNumber);

      // Count non-Unconscious characters by state → deck
      const deckCounts: Record<HeistDeckId, number> = { overt: 0, hidden: 0, disguised: 0 };
      for (const char of myChars) {
        if (char.state === 'Overt') deckCounts.overt++;
        else if (char.state === 'Hidden') deckCounts.hidden++;
        else if (char.state === 'Disguised') deckCounts.disguised++;
        // Unconscious and any other states contribute 0 cards
      }

      // Return current hand back to discard piles (played cards stay played)
      const currentHand = state.playerHands[action.playerId] ?? { cards: [], playedCards: [] };
      let handDecks = state.handDecks;
      for (const cardId of currentHand.cards) {
        const card = HEIST_CARD_BY_ID[cardId];
        if (card) handDecks = { ...handDecks, [card.deck]: returnToHandDeck(handDecks[card.deck], [cardId]) };
      }

      // Draw new cards
      let rng = state.rng;
      const newCards: string[] = [];
      for (const deckId of HEIST_DECK_IDS) {
        const n = deckCounts[deckId];
        if (n > 0) {
          const [drawn, newDeckState, nextRng] = dealFromHandDeck(handDecks[deckId], n, rng);
          handDecks = { ...handDecks, [deckId]: newDeckState };
          rng = nextRng;
          newCards.push(...drawn);
        }
      }

      return {
        ...state,
        handDecks,
        rng,
        playerHands: {
          ...state.playerHands,
          [action.playerId]: { cards: newCards, activePlayedCard: currentHand.activePlayedCard },
        },
      };
    }

    case 'playCard': {
      const hand = state.playerHands[action.playerId];
      if (!hand || !hand.cards.includes(action.cardId)) return state;
      // Return the previous active card to its deck's discard pile
      let handDecks = state.handDecks;
      if (hand.activePlayedCard) {
        const prev = HEIST_CARD_BY_ID[hand.activePlayedCard];
        if (prev) handDecks = { ...handDecks, [prev.deck]: returnToHandDeck(handDecks[prev.deck], [hand.activePlayedCard]) };
      }
      return {
        ...state,
        handDecks,
        playerHands: {
          ...state.playerHands,
          [action.playerId]: {
            cards: hand.cards.filter(id => id !== action.cardId),
            activePlayedCard: action.cardId,
          },
        },
      };
    }

    case 'reorderHand': {
      const hand = state.playerHands[action.playerId];
      if (!hand) return state;
      return {
        ...state,
        playerHands: {
          ...state.playerHands,
          [action.playerId]: { ...hand, cards: action.cards },
        },
      };
    }
  }
}

// Build a log entry recording a heist-deck draw.
function heistDrawLog(
  state: HeistCityState,
  playerId: string,
  deck: HeistDeckId,
  cardId: string | null
): HeistLogEntry {
  const card = getHeistCard(cardId);
  const player = state.players.find(p => p.id === playerId);
  return {
    id: `heist-${deck}-${Date.now()}-${cardId ?? 'empty'}`,
    timestamp: Date.now(),
    type: 'npc-action',
    playerName: player?.name ?? 'Environment',
    actionName: card ? `${deck} deck: ${card.name}` : `${deck} deck: (empty)`,
    result: card?.effect,
  };
}

export function getPlayerView(state: HeistCityState, playerId: string | null): HeistCityView {
  // No hidden information — both players see the full board
  return { ...state, viewPlayerId: playerId };
}

export function describe(config: HeistCityConfig): string {
  return [
    'Heist City — Tactical Skirmish',
    '',
    '2 players, each controlling a crew of 5 characters on a hex grid.',
    '',
    'On your turn, activate your characters one at a time. Each character can:',
    '  Move — spend movement to travel across the grid.',
    '  Attack — target an adjacent enemy; roll dice to resolve.',
    '  Hack — interact with objectives or terminals.',
    '  Use a special ability — role-specific actions.',
    'When all your characters are activated, end your turn.',
    '',
    'Victory Points: earned by controlling objectives, downing enemies, and completing escape conditions.',
    'Alert level: rises with loud actions (gunfire, alarms).',
    '',
    'Stats: M (movement), W (wounds), MS (melee skill), BS (ballistic skill), D (defense),',
    '  H (hack), G (grift). Lower is better for skill checks; higher is better for M and W.',
    '',
    'Hidden units: a Hidden character on the hex map occupies 2 adjacent hexes (a "domino").',
    '  The unit can be rotated to choose which direction the second hex extends.',
    '',
    'Disguised units: when Disguised, a character can select a persona (The Boss, Security,',
    '  or Civilian) from a dropdown. That persona\'s stat line replaces the character\'s own',
    '  stats while they remain Disguised. The Face can additionally disguise as any crew role.',
    '  Persona stats — The Boss: M4 MS12 BS12 D11 H10 G10 |',
    '  Security: M3 MS10 BS10 D11 H12 G12 | Civilian: M3 MS12 BS12 D10 H12 G12.',
    '  Wounds are unchanged from the base unit in all cases.',
    '',
    'Heist Decks: at the end of each round the environment reacts. Count every character',
    'across both teams in each state — Hidden, Disguised, and Overt — and draw one card from',
    'the deck matching the majority state. If states tie for the majority, draw from each tied',
    'deck and apply all effects. A drawn card stays active (its lasting effects remain) until',
    'the next draw from that deck. There are three decks of 8 cards: Overt, Hidden, Disguised.',
    '',
    'Note: full tactical rules and role ability cards live in the game\'s external rulebook (in progress).',
  ].join('\n');
}

export function getValidActions(_state: HeistCityState, _playerId: string): string[] {
  // Heist City has no strict turn enforcement at the module level — all action types are
  // available when the game is in progress. The UI handles which player can act when.
  return ['updateMapState', 'updateCharacter', 'loadMap', 'loadGame', 'endTurn', 'rollDice', 'updateSelection', 'updateRuler', 'updateAlert', 'drawHeistCard', 'drawRoundCards', 'resetHeistDecks', 'dealHand', 'playCard', 'reorderHand'];
}

export function getBotAction(_player: HeistCityPlayer, _state: HeistCityState): null {
  // Bot AI not implemented yet
  return null;
}
